
const ALL_SLOTS = ['7-11', '11-15', '15-19', '19-23'];

/**
 * Format date to YYYY-MM-DD string using local timezone
 * @param {Date} date - Date to format
 * @returns {string} - Date string in YYYY-MM-DD format
 */
const formatDateToLocalString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Get slots that have already passed based on current time
 * @param {Date} now - Current date/time
 * @returns {string[]} - Array of passed slots (based on start time)
 */
const getPassedSlotsForToday = (now) => {
  const currentHour = now.getHours();
  const passedSlots = [];

  ALL_SLOTS.forEach((slot) => {
    const startHour = parseInt(slot.split('-')[0], 10);
    // If current hour is >= slot start time, the slot has passed
    if (currentHour >= startHour) {
      passedSlots.push(slot);
    }
  });

  return passedSlots;
};

export const mapDataForClient = (bookings) => {
  if (!bookings || !Array.isArray(bookings)) {
    return {
      filteredBookings: [],
      fullyBookedDates: [],
      bookedSlotsByDate: {},
    };
  }

  const now = new Date();
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const todayStr = formatDateToLocalString(today);

  // Filter to only include bookings from today onwards
  const filteredBookings = bookings.filter((booking) => {
    if (!booking?.date) return false;
    const bookingDate = new Date(booking.date);
    bookingDate.setHours(0, 0, 0, 0);
    return bookingDate >= today;
  });

  // Group slots by date
  const bookedSlotsByDate = {};
  filteredBookings.forEach((booking) => {
    const { date, slot } = booking;
    if (!bookedSlotsByDate[date]) {
      bookedSlotsByDate[date] = [];
    }
    if (slot && !bookedSlotsByDate[date].includes(slot)) {
      bookedSlotsByDate[date].push(slot);
    }
  });

  // Add passed slots for today as unavailable
  const passedSlots = getPassedSlotsForToday(now);
  if (passedSlots.length > 0) {
    if (!bookedSlotsByDate[todayStr]) {
      bookedSlotsByDate[todayStr] = [];
    }
    passedSlots.forEach((slot) => {
      if (!bookedSlotsByDate[todayStr].includes(slot)) {
        bookedSlotsByDate[todayStr].push(slot);
      }
    });
  }

  // Find dates where all slots are booked (or unavailable)
  const fullyBookedDates = Object.entries(bookedSlotsByDate)
    .filter(([, slots]) => ALL_SLOTS.every((slot) => slots.includes(slot)))
    .map(([date]) => new Date(date));

  return {
    filteredBookings,
    fullyBookedDates,
    bookedSlotsByDate,
  };
};

/**
 * Check if a specific slot is booked for a given date
 * @param {Object} bookedSlotsByDate - Map of date -> booked slots
 * @param {Date|string} date - The date to check
 * @param {string} slot - The slot to check (e.g., "7-11", "11-15")
 * @returns {boolean} - true if the slot is booked
 */
export const isSlotBooked = (bookedSlotsByDate, date, slot) => {
  if (!bookedSlotsByDate || !date || !slot) return false;
  
  let dateStr;
  if (typeof date === 'string') {
    dateStr = date;
  } else {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    dateStr = `${year}-${month}-${day}`;
  }
  
  const bookedSlots = bookedSlotsByDate[dateStr];
  return bookedSlots ? bookedSlots.includes(slot) : false;
};

/**
 * Convert schedule from/to times to slot format for comparison
 * @param {string} from - Start time (e.g., "07.00")
 * @param {string} to - End time (e.g., "11.00")
 * @returns {string} - Slot format (e.g., "7-11")
 */
export const scheduleToSlotFormat = (from, to) => {
  const fromHour = parseInt(from?.split('.')[0], 10);
  const toHour = parseInt(to?.split('.')[0], 10);
  return `${fromHour}-${toHour}`;
};
